import {CSSProperties} from 'react';
import * as PropTypes from 'prop-types';
import {DateFilterItem, FeatureTooltips, LayerPrintMode} from '../../enums';
import {MapEvent} from '../../events';
import {FeatureAction, Params} from '../../types';

/**
 * Základní properties všech mapových komponent.
 *
 * @typeparam Event - Typ události kterou mapová komponenta generuje.
 */
export interface MapProps<Event extends MapEvent> {
    /**
     * Pomocné jméno pro ladíčí účely.
     * Používá se ve výpisech ladících zpráv do konzole.
     */
    debugName?: string;

    /**
     * Class jméno komponenty.
     */
    className?: string;

    /**
     * CSS styl komponenty.
     */
    style?: CSSProperties;

    /**
     * Oblast store, kde se nachází reducer komponenty.
     */
    area: string;

    /**
     * URL mapového serveru.
     * Výchozí hodnota: `window.MAPIO_URL`.
     */
    serverUrl?: string;

    /**
     * Autentizační údaje.
     *
     * Aktuálně se předává napřímo login (plain text nebo obfuskovaný pomocí
     * server funkce `CipherTools.encodeIfDecoded`), bohužel :-(.
     *
     * V budoucnu bude doufejme předěláno na autentizační token.
     */
    authentication?: string;

    /**
     * Query parametry, které budou přidány do URL mapového serveru.
     */
    queryParams?: Params;

    /**
     * Kontextové parametry, určené pro aplikační konektor na straně mapového serveru.
     */
    contextParams?: Params;

    /**
     * Odsazení ovládacích prvků mapy od okrajů stránky.
     * Čtveřice 4 `string` hodnot `[top, right, bottom, left]`.
     * Hodnota je CSS velikost, jako např. `4px`, `1rem` apod.
     */
    padding?: [string, string, string, string];

    /**
     * Dostupné položky datumového filtru.
     */
    dateFilterItems?: DateFilterItem[];

    /**
     * Akce, které lze provést nad features.
     * Jsou viditelné v detailu feature.
     */
    featureActions?: FeatureAction[];

    /**
     * Povolení zobrazení tooltipů nad features.
     * Výchozí hodnota: `true` pro editor, `false` pro zobrazovač (z důvodu zpětné kompatibility).
     */
    featureTooltips?: boolean | FeatureTooltips;

    /**
     * Příznak, zda je povoleno vkládání externích vrstev uživatelem.
     * Výchozí hodnota: `false`
     */
    externalLayersEnabled?: boolean;

    /**
     * Příznak, zda je povoleno nastavování parametrů vrstev uživatelem.
     * Výchozí hodnota: `false`
     */
    layerSettingsEnabled?: boolean;

    /**
     * Režim tisku rastrových vrstev.
     * Výchozí hodnota: {@link LayerPrintMode.TOPIC}.
     */
    rasterLayersPrintMode?: LayerPrintMode;

    /**
     * Režim tisku vektorových vrstev.
     * Výchozí hodnota: {@link LayerPrintMode.TOPIC}.
     */
    vectorLayersPrintMode?: LayerPrintMode;

    /**
     * Příznak, zda zobrazovat zaměřovač ve středu mapy.
     * Výchozí hodnota: `false`.
     */
    reticleVisible?: boolean;

    /**
     * Příznak, zda je povoleno uživatelsky přepínat téma.
     * Výchozí hodnota: `true`
     */
    topicChangeEnabled?: boolean;

    /**
     * Callback volaný v okamžiku dokončení inicializace mapové stránky ve frame.
     *
     * V tento okamžik je již možno s mapovou stránkou bezpečně komunikovat
     * s použitím dispatche akcí (např {@link FeatureActions.selectFeatures}).
     */
    onReady?: () => void;

    /**
     * Callback volaný při vzniku události mapové komponenty.
     *
     * @param event - Událost
     */
    onEvent?: (event: Event) => void;
}

/**
 * Typy základních properties všech mapových komponent.
 *
 * @ignore
 */
export const mapPropTypes = {
    debugName: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    area: PropTypes.string.isRequired,
    serverUrl: PropTypes.string,
    authentication: PropTypes.string,
    queryParams: PropTypes.object,
    contextParams: PropTypes.object,
    padding: PropTypes.arrayOf(PropTypes.string),
    dateFilterItems: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(DateFilterItem))),
    featureActions: PropTypes.array,
    featureTooltips: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(Object.values(FeatureTooltips))]),
    externalLayersEnabled: PropTypes.bool,
    layerSettingsEnabled: PropTypes.bool,
    rasterLayersPrintMode: PropTypes.oneOf(Object.values(LayerPrintMode)),
    vectorLayersPrintMode: PropTypes.oneOf(Object.values(LayerPrintMode)),
    reticleVisible: PropTypes.bool,
    onReady: PropTypes.func,
    onEvent: PropTypes.func,
};

/**
 * Výchozí hodnoty základních properties všech mapových komponent..
 *
 * @ignore
 */
export const mapDefaultProps = {
    // Zcela záměrně neuvádíme default hodnoty properties, které se synchronizují s mapio.
    // 1) Jsou definovány na straně mapio. Neduplikujeme tím pádem logiku
    //    a neposíláme zbytečné zprávy přes frame.
    // 2) V rámci zpětné kompatibility udržujeme některé deprecated properties.
    //    Default ostatních properties by je mohl přepsat (pokud by šlo o alias).
    serverUrl: (window as any).MAPIO_URL,
};
